import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { useParams } from 'react-router-dom';

// store
import { useStoreValue, storeSet } from '../../../../store';

// helpers
import { timecodeToSeconds } from '../../../../helpers/timecodeUtils';
import { getVisibleAuxLayers } from '../../../../helpers/recSession';

// hooks
import useProjectRole from '../../../../hooks/useProjectRole';

// components
import { TimelineEditor } from '../../../../components/TimelineEditor';
import RecLaneLabel from './RecLaneLabel';

const CORE_LAYER = 'CORE';
const GENERATE_LAYER = 'GENERATE_NEW';
const LAYER_GAP_UNIT = 8;

// Figma 7648:46837: two stacked timelines. The main track on top, as tall as
// the quick actions column next to it; under a divider, one thinner lane per
// collaborator, with their avatar where the quick actions would be. The split
// layout of TimelineEditor sizes both parts to fit without scrolling.
const CORE_LAYER_HEIGHT = 160;
const CORE_LAYER_GAP = LAYER_GAP_UNIT * 2;
const AUX_LAYER_HEIGHT = 55;
const AUX_LAYER_GAP = 11;

const auxLayerName = (layer) => `AUX_${layer.id}`;

const toTimelineTrack = (track, layer, cachedRecAudioUrls) => ({
    ...track,
    layer,
    start: timecodeToSeconds(track.timecode),
    duration: (track.instanceDuration?.parsedValue ?? 0) / 1000,
    actualDuration: (track.clip?.duration ?? 0) / 1000,
    startPlayback: (track.startPlayback?.parsedValue ?? 0) / 1000,
    // Only the cached blob: the clip's server URL needs the auth header, so a
    // track loaded from it stays silent — and, having no player, is never
    // reloaded when the blob arrives. Like the studio timeline, wait for it.
    audioUrl: cachedRecAudioUrls[track.clip?.id] ?? null,
    draggable: true,
    filename: track.clip?.name,
});

const buildTimelineTracks = (session, auxLayers, cachedRecAudioUrls) => [
    ...(session.coreLayer?.tracks || []).map(track =>
        toTimelineTrack(track, CORE_LAYER, cachedRecAudioUrls)),
    ...auxLayers.flatMap(auxLayer => (auxLayer.tracks || []).map(track =>
        toTimelineTrack(track, auxLayerName(auxLayer), cachedRecAudioUrls))),
];

const RecSessionTimeline = () => {
    const { projectId } = useParams();
    const recSessions = useStoreValue('recSessions');
    const selectedRecItemId = useStoreValue('selectedRecItemId');
    const selectedRecMemberId = useStoreValue('selectedRecMemberId');
    const cachedRecAudioUrls = useStoreValue('cachedRecAudioUrls');
    const timelineState = useStoreValue('timelineState');
    const timelineVolume = useStoreValue('timelineVolume');
    const timelineEditingMode = useStoreValue('timelineEditingMode');
    const { can } = useProjectRole(projectId);

    // Lane controls. Mute and solo are how you listen, not part of the session,
    // so they stay in the browser; the volume is the layer's and is saved.
    const [expandedLayers, setExpandedLayers] = useState(() => new Set());
    const [mutedLayers, setMutedLayers] = useState(() => new Set());
    const [soloedLayers, setSoloedLayers] = useState(() => new Set());
    const [volumeDrafts, setVolumeDrafts] = useState({});

    const selectedSession = useMemo(() =>
        recSessions.find(s => s.id === selectedRecItemId) ?? null,
    [recSessions, selectedRecItemId]);

    // Nobody picked in the left panel: every collaborator's lane. Somebody
    // picked: only theirs. The main track is always there.
    const visibleAuxLayers = useMemo(
        () => getVisibleAuxLayers(selectedSession, selectedRecMemberId),
        [selectedSession, selectedRecMemberId]
    );

    const auxLayersByName = useMemo(() => visibleAuxLayers.reduce(
        (acc, layer) => ({ ...acc, [auxLayerName(layer)]: layer }), {}
    ), [visibleAuxLayers]);

    const timelineTracks = useMemo(() => {
        if (!selectedSession) return [];
        return buildTimelineTracks(selectedSession, visibleAuxLayers, cachedRecAudioUrls);
    }, [selectedSession, visibleAuxLayers, cachedRecAudioUrls]);

    useEffect(() => {
        storeSet('cleanupRecSessionAudio');
        if (selectedSession) {
            storeSet('cacheRecSessionAudio', projectId, selectedSession);
        }
        setExpandedLayers(new Set());
        setMutedLayers(new Set());
        setSoloedLayers(new Set());
        setVolumeDrafts({});
    }, [selectedRecItemId]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleIn = (setter, id) => setter(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const layerVolume = useCallback((layer) => {
        const value = volumeDrafts[layer.id] ?? layer.volume;
        return Number.isFinite(value) ? value : 100;
    }, [volumeDrafts]);

    const commitVolume = useCallback((layer, value) => {
        // Without the right to edit the session the change stays a local mix.
        if (!can.manageTeam || !selectedSession) return;
        storeSet('updateRecSessionLayerVolume', projectId, selectedSession.id, layer.id, value);
    }, [can.manageTeam, projectId, selectedSession]);

    const layers = useMemo(() => {
        const base = [CORE_LAYER, ...visibleAuxLayers.map(auxLayerName)];
        return timelineEditingMode === 'generateInstrument' ? [...base, GENERATE_LAYER] : base;
    }, [visibleAuxLayers, timelineEditingMode]);

    const layerHeightMap = useMemo(() => layers.reduce((acc, name) => ({
        ...acc,
        [name]: name === CORE_LAYER ? CORE_LAYER_HEIGHT : AUX_LAYER_HEIGHT,
    }), {}), [layers]);

    const layerGapMap = useMemo(() => layers.reduce((acc, name) => ({
        ...acc,
        [name]: name === CORE_LAYER ? CORE_LAYER_GAP : AUX_LAYER_GAP,
    }), {}), [layers]);

    const isPlaying = timelineState === 'playing';
    const [controlledTime, setControlledTime] = useState(null);
    const timelineRef = useRef({ duration: 0 });

    const handlePlaybackChange = useCallback((playing) => {
        storeSet('timelineState', playing ? 'playing' : 'paused');
    }, []);

    const handleTimeChange = useCallback((time) => {
        timelineRef.current.time = time;
    }, []);

    const handleDurationChange = useCallback((duration) => {
        timelineRef.current.duration = duration;
    }, []);

    useEffect(() => {
        if (timelineState === 'stopped') {
            setControlledTime(0);
            storeSet('timelineState', 'paused');
        } else if (timelineState === 'backward') {
            setControlledTime(0);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        } else if (timelineState === 'forward') {
            setControlledTime(timelineRef.current.duration || 999999);
            storeSet('timelineState', isPlaying ? 'playing' : 'paused');
        }
    }, [timelineState, isPlaying]);

    useEffect(() => {
        if (controlledTime !== null) {
            const timer = setTimeout(() => setControlledTime(null), 100);
            return () => clearTimeout(timer);
        }
    }, [controlledTime]);

    const handleLayerSelection = useCallback(({ layer, startPos, endPos }) => {
        storeSet('stemSplitterSelection', { layer, startPos, endPos, timelineType: 'rec' });
    }, []);

    const handleGenerateLayerSelection = useCallback(({ layer, startPos, endPos }) => {
        storeSet('generateInstrumentSelection', { layer, startPos, endPos, timelineType: 'rec' });
    }, []);

    // Solo narrows the collaborator lanes to the soloed ones; the main track
    // keeps playing, it is what everybody records against.
    const getTrackVolume = useCallback((track) => {
        if (timelineVolume === 0) return 0;
        const masterVol = timelineVolume / 100;
        const auxLayer = auxLayersByName[track.layer];
        if (!auxLayer) return masterVol;
        if (mutedLayers.has(auxLayer.id)) return 0;
        if (soloedLayers.size > 0 && !soloedLayers.has(auxLayer.id)) return 0;
        return masterVol * (layerVolume(auxLayer) / 100);
    }, [timelineVolume, auxLayersByName, mutedLayers, soloedLayers, layerVolume]);

    // Next to each collaborator lane, in the side column: who it belongs to,
    // opening into its mute / solo / volume controls.
    const renderLayerLabel = useCallback((layerName) => {
        const auxLayer = auxLayersByName[layerName];
        if (!auxLayer) return null;

        const id = auxLayer.id;
        return (
            <RecLaneLabel
                layer={auxLayer}
                expanded={expandedLayers.has(id)}
                onToggle={() => toggleIn(setExpandedLayers, id)}
                muted={mutedLayers.has(id)}
                soloed={soloedLayers.has(id)}
                onMute={() => toggleIn(setMutedLayers, id)}
                onSolo={() => toggleIn(setSoloedLayers, id)}
                volume={layerVolume(auxLayer)}
                onVolumeChange={value => setVolumeDrafts(prev => ({ ...prev, [id]: value }))}
                onVolumeCommit={value => commitVolume(auxLayer, value)}
            />
        );
    }, [auxLayersByName, expandedLayers, mutedLayers, soloedLayers, layerVolume, commitVolume]);

    // An empty lane says what it is waiting for: a session starts without a
    // main track, and a collaborator's lane stays empty until they upload.
    const hasMainTrack = Boolean(selectedSession?.coreLayer?.tracks?.length);

    const renderLayerOverlay = useCallback((layerName) => {
        if (layerName === CORE_LAYER) {
            return hasMainTrack ? null : (
                <span className="recSessionTimeline__laneEmpty recSessionTimeline__laneEmpty--main">
                    {__('Nessuna traccia principale — caricala con "Aggiungi traccia principale"', 'soundbetween')}
                </span>
            );
        }

        const auxLayer = auxLayersByName[layerName];
        // The open lane controls cover the start of the lane, where it would be.
        if (!auxLayer || (auxLayer.tracks || []).length > 0 || expandedLayers.has(auxLayer.id)) return null;

        return (
            <span className="recSessionTimeline__laneEmpty">
                {auxLayer.assignedUser
                    ? __('In attesa della traccia', 'soundbetween')
                    : __('Nessuna traccia', 'soundbetween')}
            </span>
        );
    }, [auxLayersByName, hasMainTrack, expandedLayers]);

    return (
        <>
            <span className="recSessionTitle">
                {__('Recording Session', 'soundbetween')}
            </span>
            {!selectedSession
                ? <div className="recSessionTimeline__empty">
                    <span className="recSessionTimeline__emptyText">
                        {__('Select a recording session', 'soundbetween')}
                    </span>
                  </div>
                : <TimelineEditor
                    key={selectedSession.id}
                    mode="rec"
                    tracks={timelineTracks}
                    layers={layers}
                    layerHeightMap={layerHeightMap}
                    layerGapMap={layerGapMap}
                    renderLayerOverlay={renderLayerOverlay}
                    splitAfterLayer={CORE_LAYER}
                    renderLayerLabel={renderLayerLabel}
                    hideVideo
                    isPlayingExternal={isPlaying}
                    currentTimeExternal={controlledTime}
                    timelineEditingMode={timelineEditingMode}
                    getTrackVolume={getTrackVolume}
                    onPlaybackChange={handlePlaybackChange}
                    onTimeChange={handleTimeChange}
                    onDurationChange={handleDurationChange}
                    onLayerSelectionComplete={handleLayerSelection}
                    generateInstrumentLayer={timelineEditingMode === 'generateInstrument' ? GENERATE_LAYER : null}
                    onGenerateLayerSelectionComplete={handleGenerateLayerSelection}
                  />
            }
        </>
    );
};

export default RecSessionTimeline;
